import { hydrateAircraftDatabase, type AircraftDatabaseDesk } from "./aircraftDatabase";
import { seedFleet, starterScenery } from "./airports";
import { dropCommunityScanStubs } from "./sceneryScan";
import { hydrateDutyRoster, type DutyRoster } from "./duty";
import type { GroundingHold } from "./flightGrounding";
import { hydrateHeroStrip, publishHeroStrip, type HeroStripPublish } from "./heroStrip";
import {
  catalogsAsStubs,
  catalogsFromUnknown,
  formatMoney,
  hydrateCurrency,
  hydrateMoneyCatalogs,
  icaosFromScenery,
  type CurrencyCode,
  type MoneyCatalogs,
} from "./catalogs";
import { hydrateJetAOverrides, type JetAOverride } from "./jetA";
import { hydrateAirportManagerRecords, type AirportManagerRecord } from "./airportManager";
import { formatUtcDateTime } from "./clock";
import { hydrateMissions } from "./missionHydrate";
import { safeTrim } from "./text";
import { hydrateAccount, hydrateRates } from "./ledger";
import { hydrateLogbook } from "./logbook";
import { hydrateMelItems } from "./mel";
import { hydrateFleet, refreshFleetStatus } from "./maintenance";
import { hydrateCompany } from "./company";
import { hydrateMainLease } from "./deviceRole";
import { reconcileInForceDuty } from "./ftlScheme";
import { stampMissionsAndCompany } from "./flightNumber";
import { liveCompanyInbox } from "./companySync";
import { careerBrandingForSync } from "./airlineBrand";
import { hydrateCareer } from "./career";
import { hydrateCrewRoster } from "./crew";
import { DEFAULT_PILOT, normalizeRatedTypes } from "./profile";
import type {
  AircraftSpec,
  CareerState,
  CharterRequest,
  CompanyAccount,
  CompanyProfile,
  CrewMember,
  DispatchMode,
  FlightLogEntry,
  MelItem,
  Mission,
  PilotLogbookEntry,
  PilotProfile,
  RateTables,
  MapboxSettings,
  NotamSettings,
  SayIntentionsSettings,
  SceneryAirport,
  SimBriefSettings,
  Squawk,
  ThemeId,
  ThemeAccentId,
  ThemePackId,
  DutyStandbyState,
  VoiceSettings,
} from "./types";
import { hydrateDutyStandby, hydrateVoiceSettings } from "./voiceDesk";
import { hydratePhoneSettings, redactPhoneSettings, type PhoneSettings } from "./phone";
import { emptyDutyStandby } from "./dutyStandby";
import { hydrateInboxList } from "./voiceInbox";
import { hydrateFlightTracks, type FlightTrack } from "./aar";
import { hydrateFlightLogs } from "./flightlog";
import { hydrateJobMode, type JobMode } from "./jobMode";
import {
  hydrateAssignmentAudit,
  hydrateDeclineCooldowns,
  hydrateTripAssignments,
  type AssignmentAuditEvent,
  type DeclineCooldown,
  type TripAssignment,
} from "./assignment";
import {
  DEFAULT_SIMCONNECT,
  hydrateFusedPosition,
  hydrateSimConnectSettings,
  type FusedPosition,
  type SimConnectSettings,
} from "./position";
import { hydrateCrewPresenceList, type CrewPresence } from "./crewPresence";
import { hydrateSopOverrides, type SopOverrides } from "./sops";
import { hydrateAutoInboxSettings, type AutoInboxSettings } from "./autoInbox";
import { hydrateConnectedDevices } from "./connectedDevices";
import { deskChromeFromUnknown, type DeskChrome } from "./themePacks";
import { hydrateDispatchPlan, type DispatchPlan } from "./dispatchBrain";
import {
  hydrateDispatcherBrain,
  hydrateGrokBrainCheckpoint,
  hydrateGrokBrainReviews,
  resolveDispatcherBrainOnLoad,
  type DispatcherBrainMode,
  type GrokBrainCheckpoint,
  type GrokBrainReview,
} from "./grokDispatchBrain";
import { hydrateWeightBalanceDesk, type WeightBalanceDesk } from "./weightBalance";
import { hydrateNotamBrief, type NotamBrief } from "./notam";
import {
  DEFAULT_MAPBOX,
  hydrateMapboxSettings,
  isMapboxSecretToken,
  publishMapboxSettings,
} from "./mapboxSettings";

/** Snapshot document kind — reject anything else on import / pull. */
export const SYNC_SNAPSHOT_KIND = "northstar.dispatch.snapshot";

/** Bump when the payload shape is not backward-compatible. */
export const SYNC_SNAPSHOT_VERSION = 1 as const;

export const DEFAULT_SIMBRIEF: SimBriefSettings = {
  username: "",
  pilotId: "",
  apiKey: "",
};

export const DEFAULT_SI: SayIntentionsSettings = {
  enabled: true,
  apiKey: "",
  vaApiKey: "",
};

export const DEFAULT_NOTAM: NotamSettings = {
  checkwxApiKey: "",
  faaClientId: "",
  faaClientSecret: "",
};

export function hydrateNotamSettings(raw?: Partial<NotamSettings> | null): NotamSettings {
  return {
    ...DEFAULT_NOTAM,
    checkwxApiKey: typeof raw?.checkwxApiKey === "string" ? raw.checkwxApiKey : "",
    faaClientId: typeof raw?.faaClientId === "string" ? raw.faaClientId : "",
    faaClientSecret: typeof raw?.faaClientSecret === "string" ? raw.faaClientSecret : "",
  };
}

export { DEFAULT_MAPBOX, hydrateMapboxSettings, publishMapboxSettings };

export const SECRET_REDACTED = "";

/** Persist slice worth backing up or pushing to a later Linode stub. */
export type SyncSnapshotPayload = {
  theme: ThemeId;
  themePack?: ThemePackId;
  themeAccent?: ThemeAccentId;
  /** Stable chrome object for companion / mobile. Same values as theme* + company. */
  deskChrome?: DeskChrome;
  /** Desks that have published. Main and Secondary both appear. Not a secret. */
  connectedDevices?: import("./connectedDevices").ConnectedDevice[];
  /** Server-owned Main lease. Absent on older Nanode snapshots. */
  mainLease?: import("./deviceRole").MainLease | null;
  dispatchMode: DispatchMode;
  profile: PilotProfile;
  company: CompanyProfile;
  crew: CrewMember[];
  scenery: SceneryAirport[];
  /** Canonical tickets (CEL#### / F=ferry). Trip Support lives on each Mission. */
  missions: Mission[];
  fleet: AircraftSpec[];
  /** Aircraft performance database. User rows sit on the shipped base. */
  aircraftDatabase?: AircraftDatabaseDesk;
  /** RFQs only — not a second schedule. Support is not stored here. */
  inbox: CharterRequest[];
  /** Trip Log projection of `missions[]`. Same ticket ids — not a Support list. */
  flightLogs: FlightLogEntry[];
  account: CompanyAccount;
  rates: RateTables;
  catalogs: MoneyCatalogs;
  /** Dispatcher Settings Jet A overrides — company / Push so every desk sees the same number. */
  jetAOverrides?: JetAOverride[];
  /** Airport Manager persisted edits — company / Push so every desk sees the same data. */
  airportManager?: AirportManagerRecord[];
  squawks: Squawk[];
  melItems: MelItem[];
  logbook: PilotLogbookEntry[];
  duty: DutyRoster;
  career: CareerState;
  dutyStandby?: DutyStandbyState;
  voiceSettings?: VoiceSettings;
  phoneSettings?: PhoneSettings;
  jobMode?: JobMode;
  assignments?: TripAssignment[];
  assignmentAudit?: AssignmentAuditEvent[];
  declineCooldowns?: DeclineCooldown[];
  simconnect?: SimConnectSettings;
  lastKnownPosition?: FusedPosition | null;
  /** Per-crew last published fix. Shared on Push/Pull — not live SimConnect. */
  crewPresence?: CrewPresence[];
  /** Confirm-gated dispatcher brain plan — company board state, not solo. */
  dispatchPlan?: DispatchPlan | null;
  dispatcherBrain?: DispatcherBrainMode;
  dispatcherBrainUserSet?: boolean;
  grokBrainCheckpoint?: GrokBrainCheckpoint | null;
  brainReviews?: GrokBrainReview[];
  flightTracks?: FlightTrack[];
  sopOverrides?: SopOverrides;
  weightBalance?: WeightBalanceDesk;
  inboxModel?: string;
  autoInbox?: AutoInboxSettings;
  /** Empty unless the snapshot was exported with secrets. */
  xaiApiKey: string;
  simbrief: SimBriefSettings;
  sayintentions: SayIntentionsSettings;
  notamSettings?: NotamSettings;
  /**
   * Public Mapbox token (`pk.…`) + style. Published so Mobile can load the same
   * GL map. Secret `sk.` tokens are stripped. Never commit.
   */
  mapboxSettings?: MapboxSettings;
  /**
   * Filtered flight-affecting NOTAM brief (no API keys). Desk writes this after
   * fetchNotamChain so Mobile / other desks can show last-known when AviationAPI
   * is down. Never a raw dump.
   */
  opsNotamBrief?: NotamBrief | null;
  /**
   * Desk hero counts, Zulu departure, and alert tiers for the companion.
   * Device snooze and acknowledge stay on the desk. Older snapshots omit this.
   */
  heroStrip?: HeroStripPublish | null;
};

export type SyncSnapshot = {
  kind: typeof SYNC_SNAPSHOT_KIND;
  version: typeof SYNC_SNAPSHOT_VERSION;
  exportedAtUtc: string;
  includeSecrets: boolean;
  payload: SyncSnapshotPayload;
  /** Desk semver that published this snapshot. Older desks must not overwrite. */
  deskVersion?: string;
  /** Dispatch sim desk is the only Push authority. Followers Pull. */
  boardAuthority?: "dispatch" | "follower";
};

export type SnapshotSource = Omit<SyncSnapshotPayload, "career" | "catalogs"> & {
  career?: CareerState;
  catalogs?: MoneyCatalogs;
  /** Desk-only holds. Published inside heroStrip. Not stored as their own list. */
  groundingHolds?: GroundingHold[];
};

export type ParseSnapshotResult =
  | { ok: true; snapshot: SyncSnapshot }
  | { ok: false; error: string };

export type BuildSnapshotOptions = {
  includeSecrets?: boolean;
  now?: Date;
  deskVersion?: string;
  boardAuthority?: "dispatch" | "follower";
};

function hydrateProfile(raw?: Partial<PilotProfile>): PilotProfile {
  const name = raw?.name && raw.name !== "Desk" ? raw.name : DEFAULT_PILOT.name;
  return {
    ...DEFAULT_PILOT,
    ...raw,
    name,
    ratedIcaoTypes: normalizeRatedTypes(raw?.ratedIcaoTypes),
  };
}

function redactSimbrief(settings: SimBriefSettings): SimBriefSettings {
  return { ...DEFAULT_SIMBRIEF, ...settings, apiKey: SECRET_REDACTED };
}

function redactSayIntentions(settings: SayIntentionsSettings): SayIntentionsSettings {
  return {
    ...DEFAULT_SI,
    ...settings,
    apiKey: SECRET_REDACTED,
    vaApiKey: SECRET_REDACTED,
  };
}

function redactNotamSettings(settings: NotamSettings | undefined): NotamSettings {
  return {
    ...DEFAULT_NOTAM,
    ...settings,
    checkwxApiKey: SECRET_REDACTED,
    faaClientId: SECRET_REDACTED,
    faaClientSecret: SECRET_REDACTED,
  };
}

/** True when the payload still carries a raw API key. */
export function payloadHasSecrets(payload: Partial<SyncSnapshotPayload> | undefined): boolean {
  if (!payload) return false;
  return Boolean(
    payload.xaiApiKey?.trim() ||
      payload.simbrief?.apiKey?.trim() ||
      payload.sayintentions?.apiKey?.trim() ||
      payload.sayintentions?.vaApiKey?.trim() ||
      payload.notamSettings?.checkwxApiKey?.trim() ||
      payload.notamSettings?.faaClientId?.trim() ||
      payload.notamSettings?.faaClientSecret?.trim() ||
      isMapboxSecretToken(payload.mapboxSettings?.accessToken ?? "") ||
      payload.phoneSettings?.twilioAuthToken?.trim() ||
      payload.phoneSettings?.twilioAccountSid?.trim(),
  );
}

export function redactPayloadSecrets(payload: SyncSnapshotPayload): SyncSnapshotPayload {
  return {
    ...payload,
    xaiApiKey: SECRET_REDACTED,
    simbrief: redactSimbrief(payload.simbrief),
    sayintentions: redactSayIntentions(payload.sayintentions),
    notamSettings: redactNotamSettings(payload.notamSettings),
    mapboxSettings: publishMapboxSettings(payload.mapboxSettings),
    phoneSettings: payload.phoneSettings ? redactPhoneSettings(payload.phoneSettings) : payload.phoneSettings,
  };
}

export function buildSnapshot(source: SnapshotSource, opts: BuildSnapshotOptions = {}): SyncSnapshot {
  const includeSecrets = Boolean(opts.includeSecrets);
  const now = opts.now ?? new Date();
  const chrome = deskChromeFromUnknown({
    deskChrome: source.deskChrome,
    theme: source.theme,
    themePack: source.themePack,
    themeAccent: source.themeAccent,
    company: source.company,
  });
  const raw: SyncSnapshotPayload = {
    theme: chrome.theme,
    themePack: chrome.themePack,
    themeAccent: chrome.themeAccent,
    deskChrome: chrome,
    dispatchMode: source.dispatchMode,
    profile: source.profile,
    company: hydrateCompany(
      { ...source.company, ...chrome },
      { legacyPersist: Boolean(source.company) },
    ),
    crew: hydrateCrewRoster(source.crew, source.profile, { seedIfEmpty: false }),
    connectedDevices: hydrateConnectedDevices(source.connectedDevices),
    ...(hydrateMainLease(source.mainLease) ? { mainLease: hydrateMainLease(source.mainLease) } : {}),
    scenery: dropCommunityScanStubs(source.scenery),
    missions: source.missions,
    fleet: source.fleet,
    aircraftDatabase: source.aircraftDatabase ? hydrateAircraftDatabase(source.aircraftDatabase) : undefined,
    inbox: liveCompanyInbox(source.inbox),
    flightLogs: source.flightLogs,
    account: source.account,
    rates: source.rates,
    catalogs: hydrateMoneyCatalogs(source.catalogs ?? catalogsFromUnknown(source.career?.catalogStubs), {
      icaos: icaosFromScenery(source.scenery),
      rates: source.rates,
    }),
    jetAOverrides: hydrateJetAOverrides(source.jetAOverrides),
    airportManager: hydrateAirportManagerRecords(source.airportManager),
    squawks: source.squawks,
    melItems: hydrateMelItems(source.melItems),
    logbook: hydrateLogbook(source.logbook),
    duty: hydrateDutyRoster(source.duty),
    career: careerBrandingForSync(
      hydrateCareer(
        {
          ...source.career,
          catalogStubs: catalogsAsStubs(
            hydrateMoneyCatalogs(source.catalogs ?? catalogsFromUnknown(source.career?.catalogStubs), {
              icaos: icaosFromScenery(source.scenery),
              rates: source.rates,
            }),
          ),
        },
        { companyConfigured: Boolean(source.company?.setupComplete) },
      ),
    ),
    dutyStandby: hydrateDutyStandby(source.dutyStandby),
    voiceSettings: hydrateVoiceSettings(source.voiceSettings),
    phoneSettings: hydratePhoneSettings(source.phoneSettings),
    jobMode: hydrateJobMode(source.jobMode),
    assignments: hydrateTripAssignments(source.assignments),
    assignmentAudit: hydrateAssignmentAudit(source.assignmentAudit),
    declineCooldowns: hydrateDeclineCooldowns(source.declineCooldowns),
    simconnect: hydrateSimConnectSettings(source.simconnect),
    lastKnownPosition: hydrateFusedPosition(source.lastKnownPosition),
    crewPresence: hydrateCrewPresenceList(source.crewPresence),
    dispatchPlan: hydrateDispatchPlan(source.dispatchPlan),
    dispatcherBrain: hydrateDispatcherBrain(source.dispatcherBrain),
    dispatcherBrainUserSet: source.dispatcherBrainUserSet === true,
    grokBrainCheckpoint: hydrateGrokBrainCheckpoint(source.grokBrainCheckpoint),
    brainReviews: hydrateGrokBrainReviews(source.brainReviews),
    flightTracks: hydrateFlightTracks(source.flightTracks),
    sopOverrides: hydrateSopOverrides(source.sopOverrides),
    weightBalance: hydrateWeightBalanceDesk(source.weightBalance),
    inboxModel: typeof source.inboxModel === "string" ? source.inboxModel : "",
    autoInbox: hydrateAutoInboxSettings(source.autoInbox),
    xaiApiKey: source.xaiApiKey ?? "",
    simbrief: { ...DEFAULT_SIMBRIEF, ...source.simbrief },
    sayintentions: { ...DEFAULT_SI, ...source.sayintentions },
    notamSettings: hydrateNotamSettings(source.notamSettings),
    mapboxSettings: publishMapboxSettings(source.mapboxSettings),
    opsNotamBrief: hydrateNotamBrief(source.opsNotamBrief),
  };
  const aligned = reconcileInForceDuty(raw.company, raw.duty.settings);
  raw.company = aligned.company;
  raw.duty = { ...raw.duty, settings: aligned.duty };
  raw.heroStrip = publishHeroStrip({
    now,
    missions: raw.missions,
    fleet: raw.fleet,
    crew: raw.crew,
    duty: raw.duty,
    assignments: raw.assignments,
    scenery: raw.scenery,
    company: raw.company,
    profileName: raw.profile?.name,
    dispatchPlan: raw.dispatchPlan,
    groundingHolds: source.groundingHolds ?? [],
    squawks: raw.squawks,
    melItems: raw.melItems,
    inbox: raw.inbox,
  });
  return {
    kind: SYNC_SNAPSHOT_KIND,
    version: SYNC_SNAPSHOT_VERSION,
    exportedAtUtc: now.toISOString(),
    includeSecrets,
    payload: includeSecrets ? raw : redactPayloadSecrets(raw),
    deskVersion: typeof opts.deskVersion === "string" ? opts.deskVersion.trim() : undefined,
    boardAuthority: opts.boardAuthority === "follower" ? "follower" : opts.boardAuthority === "dispatch" ? "dispatch" : undefined,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

export function parseSnapshot(input: unknown): ParseSnapshotResult {
  let value = input;
  if (typeof input === "string") {
    try {
      value = JSON.parse(input) as unknown;
    } catch {
      return { ok: false, error: "Backup is not valid JSON." };
    }
  }
  if (!isRecord(value)) {
    return { ok: false, error: "Backup is not a snapshot object." };
  }
  if (value.kind !== SYNC_SNAPSHOT_KIND) {
    return { ok: false, error: "File is not a Northstar dispatch snapshot." };
  }
  if (value.version !== SYNC_SNAPSHOT_VERSION) {
    return { ok: false, error: `Unsupported snapshot version (${String(value.version)}).` };
  }
  if (!isRecord(value.payload)) {
    return { ok: false, error: "Snapshot is missing payload." };
  }
  const payload = hydrateSnapshotPayload(value.payload);
  return {
    ok: true,
    snapshot: {
      kind: SYNC_SNAPSHOT_KIND,
      version: SYNC_SNAPSHOT_VERSION,
      exportedAtUtc: typeof value.exportedAtUtc === "string" ? value.exportedAtUtc : new Date().toISOString(),
      includeSecrets: Boolean(value.includeSecrets) && payloadHasSecrets(payload),
      payload,
      deskVersion: typeof value.deskVersion === "string" ? value.deskVersion.trim() : undefined,
      boardAuthority: value.boardAuthority === "follower" ? "follower" : value.boardAuthority === "dispatch" ? "dispatch" : undefined,
    },
  };
}

export function hydrateSnapshotPayload(raw: Partial<SyncSnapshotPayload>): SyncSnapshotPayload {
  const payload = hydrateSnapshotPayloadBody(raw);
  const aligned = reconcileInForceDuty(payload.company, payload.duty.settings);
  return {
    ...payload,
    company: aligned.company,
    duty: { ...payload.duty, settings: aligned.duty },
  };
}

function hydrateSnapshotPayloadBody(raw: Partial<SyncSnapshotPayload>): SyncSnapshotPayload {
  const squawks = Array.isArray(raw.squawks) ? raw.squawks : [];
  const melItems = hydrateMelItems(raw.melItems);
  const logbook = hydrateLogbook(raw.logbook);
  const fleet = refreshFleetStatus(hydrateFleet(raw.fleet, seedFleet), squawks, melItems);
  const chrome = deskChromeFromUnknown(raw);
  const numbered = stampMissionsAndCompany(
    hydrateMissions(raw.missions),
    hydrateCompany(
      { ...raw.company, ...chrome },
      {
        legacyPersist: Boolean(raw.company) && raw.company?.setupComplete !== false,
      },
    ),
  );
  return {
    theme: chrome.theme,
    themePack: chrome.themePack,
    themeAccent: chrome.themeAccent,
    deskChrome: chrome,
    connectedDevices: hydrateConnectedDevices(raw.connectedDevices),
    ...(hydrateMainLease(raw.mainLease) ? { mainLease: hydrateMainLease(raw.mainLease) } : {}),
    dispatchMode: raw.dispatchMode === "manual" ? "manual" : "ai",
    profile: hydrateProfile(raw.profile),
    company: numbered.company,
    crew: hydrateCrewRoster(raw.crew, hydrateProfile(raw.profile), {
      seedIfEmpty: Boolean(raw.company?.setupComplete || raw.company?.operatorName),
    }),
    scenery: raw.scenery?.length ? raw.scenery : starterScenery(),
    missions: numbered.missions,
    fleet,
    aircraftDatabase: raw.aircraftDatabase != null ? hydrateAircraftDatabase(raw.aircraftDatabase) : undefined,
    inbox: hydrateInboxList(raw.inbox as CharterRequest[] | undefined),
    flightLogs: hydrateFlightLogs(raw.flightLogs),
    account: hydrateAccount(raw.account),
    rates: hydrateRates(raw.rates),
    catalogs: hydrateMoneyCatalogs(raw.catalogs ?? catalogsFromUnknown(raw.career?.catalogStubs), {
      icaos: icaosFromScenery(raw.scenery),
      rates: hydrateRates(raw.rates),
    }),
    jetAOverrides: hydrateJetAOverrides(raw.jetAOverrides),
    airportManager: hydrateAirportManagerRecords(raw.airportManager),
    squawks,
    melItems,
    logbook,
    duty: hydrateDutyRoster(raw.duty),
    career: hydrateCareer(
      {
        ...raw.career,
        catalogStubs: catalogsAsStubs(
          hydrateMoneyCatalogs(raw.catalogs ?? catalogsFromUnknown(raw.career?.catalogStubs), {
            icaos: icaosFromScenery(raw.scenery),
            rates: hydrateRates(raw.rates),
          }),
        ),
      },
      {
        companyConfigured: Boolean(raw.company?.setupComplete || raw.company?.operatorName),
      },
    ),
    dutyStandby: raw.dutyStandby ? hydrateDutyStandby(raw.dutyStandby) : emptyDutyStandby(),
    voiceSettings: hydrateVoiceSettings(raw.voiceSettings),
    phoneSettings: hydratePhoneSettings(raw.phoneSettings),
    jobMode: hydrateJobMode(raw.jobMode),
    assignments: hydrateTripAssignments(raw.assignments),
    assignmentAudit: hydrateAssignmentAudit(raw.assignmentAudit),
    declineCooldowns: hydrateDeclineCooldowns(raw.declineCooldowns),
    simconnect: hydrateSimConnectSettings(raw.simconnect ?? DEFAULT_SIMCONNECT),
    lastKnownPosition: hydrateFusedPosition(raw.lastKnownPosition),
    crewPresence: hydrateCrewPresenceList(raw.crewPresence),
    dispatchPlan: hydrateDispatchPlan(raw.dispatchPlan),
    ...(() => {
      const resolved = resolveDispatcherBrainOnLoad({
        raw: raw.dispatcherBrain,
        userSet: raw.dispatcherBrainUserSet,
        hasCheckpoint: Boolean(raw.grokBrainCheckpoint),
      });
      return { dispatcherBrain: resolved.mode, dispatcherBrainUserSet: resolved.userSet };
    })(),
    grokBrainCheckpoint: hydrateGrokBrainCheckpoint(raw.grokBrainCheckpoint),
    brainReviews: hydrateGrokBrainReviews(raw.brainReviews),
    flightTracks: hydrateFlightTracks(raw.flightTracks),
    sopOverrides: hydrateSopOverrides(raw.sopOverrides),
    weightBalance: hydrateWeightBalanceDesk(raw.weightBalance),
    inboxModel: typeof raw.inboxModel === "string" ? raw.inboxModel : "",
    autoInbox: hydrateAutoInboxSettings(raw.autoInbox),
    xaiApiKey: typeof raw.xaiApiKey === "string" ? raw.xaiApiKey : "",
    simbrief: {
      ...DEFAULT_SIMBRIEF,
      ...raw.simbrief,
      username: typeof raw.simbrief?.username === "string" ? raw.simbrief.username : DEFAULT_SIMBRIEF.username,
      pilotId: typeof raw.simbrief?.pilotId === "string" ? raw.simbrief.pilotId : DEFAULT_SIMBRIEF.pilotId,
      apiKey: typeof raw.simbrief?.apiKey === "string" ? raw.simbrief.apiKey : DEFAULT_SIMBRIEF.apiKey,
    },
    sayintentions: {
      ...DEFAULT_SI,
      ...raw.sayintentions,
      apiKey: typeof raw.sayintentions?.apiKey === "string" ? raw.sayintentions.apiKey : DEFAULT_SI.apiKey,
      vaApiKey: typeof raw.sayintentions?.vaApiKey === "string" ? raw.sayintentions.vaApiKey : DEFAULT_SI.vaApiKey,
    },
    notamSettings: hydrateNotamSettings(raw.notamSettings),
    mapboxSettings: hydrateMapboxSettings(raw.mapboxSettings),
    opsNotamBrief: hydrateNotamBrief(raw.opsNotamBrief),
    heroStrip: hydrateHeroStrip(raw.heroStrip),
  };
}

export type ApplySnapshotLocalDeskPrefs = {
  /** Settings → Desk Dispatcher brain. Per-PC — not a company board field. */
  dispatcherBrain: DispatcherBrainMode;
  dispatcherBrainUserSet?: boolean;
  /** Settings Inbox / Grok model override. Same PC as the xAI key. */
  inboxModel?: string;
  grokBrainCheckpoint?: GrokBrainCheckpoint | null;
  brainReviews?: GrokBrainReview[];
};

export type ApplySnapshotOptions = {
  /** When the incoming snapshot redacted keys, keep the desk’s existing secrets. */
  localSecrets?: {
    xaiApiKey: string;
    simbrief: SimBriefSettings;
    sayintentions: SayIntentionsSettings;
    notamSettings?: NotamSettings;
    mapboxSettings?: MapboxSettings;
    phoneSettings?: PhoneSettings;
  };
  /**
   * Brain mode + Inbox model stay on this PC. Live Pull / company snapshot
   * must not flip Classic → Grok (that starts xAI schedule calls on a desk
   * that never turned the brain on, often with no key).
   */
  localDeskPrefs?: ApplySnapshotLocalDeskPrefs;
};

function withLocalDeskPrefs(
  payload: SyncSnapshotPayload,
  prefs?: ApplySnapshotLocalDeskPrefs,
): SyncSnapshotPayload {
  if (!prefs) return payload;
  return {
    ...payload,
    dispatcherBrain: hydrateDispatcherBrain(prefs.dispatcherBrain),
    dispatcherBrainUserSet:
      prefs.dispatcherBrainUserSet !== undefined
        ? prefs.dispatcherBrainUserSet === true
        : payload.dispatcherBrainUserSet,
    inboxModel: typeof prefs.inboxModel === "string" ? prefs.inboxModel : payload.inboxModel,
    grokBrainCheckpoint:
      prefs.grokBrainCheckpoint !== undefined ? prefs.grokBrainCheckpoint : payload.grokBrainCheckpoint,
    brainReviews: prefs.brainReviews !== undefined ? prefs.brainReviews : payload.brainReviews,
  };
}

/** Merge incoming snapshot onto local secrets when the file did not include keys. */
export function applySnapshotPayload(
  snapshot: SyncSnapshot,
  opts: ApplySnapshotOptions = {},
): SyncSnapshotPayload {
  const incoming = hydrateSnapshotPayload(snapshot.payload);
  const local = opts.localSecrets;
  if (!local || snapshot.includeSecrets) return withLocalDeskPrefs(incoming, opts.localDeskPrefs);
  return withLocalDeskPrefs({
    ...incoming,
    xaiApiKey: safeTrim(incoming.xaiApiKey) ? incoming.xaiApiKey : local.xaiApiKey,
    simbrief: {
      ...incoming.simbrief,
      apiKey: safeTrim(incoming.simbrief?.apiKey) ? incoming.simbrief.apiKey : local.simbrief.apiKey,
    },
    sayintentions: {
      ...incoming.sayintentions,
      apiKey: safeTrim(incoming.sayintentions?.apiKey) ? incoming.sayintentions.apiKey : local.sayintentions.apiKey,
      vaApiKey: safeTrim(incoming.sayintentions?.vaApiKey)
        ? incoming.sayintentions.vaApiKey
        : local.sayintentions.vaApiKey,
    },
    notamSettings: {
      ...incoming.notamSettings,
      checkwxApiKey: incoming.notamSettings?.checkwxApiKey?.trim()
        ? incoming.notamSettings.checkwxApiKey
        : (local.notamSettings?.checkwxApiKey ?? ""),
      faaClientId: incoming.notamSettings?.faaClientId?.trim()
        ? incoming.notamSettings.faaClientId
        : (local.notamSettings?.faaClientId ?? ""),
      faaClientSecret: incoming.notamSettings?.faaClientSecret?.trim()
        ? incoming.notamSettings.faaClientSecret
        : (local.notamSettings?.faaClientSecret ?? ""),
    },
    mapboxSettings: incoming.mapboxSettings?.accessToken?.trim()
      ? incoming.mapboxSettings
      : hydrateMapboxSettings({
          ...incoming.mapboxSettings,
          accessToken: local.mapboxSettings?.accessToken ?? "",
        }),
    phoneSettings: incoming.phoneSettings?.twilioAuthToken?.trim()
      ? incoming.phoneSettings
      : hydratePhoneSettings({
          ...incoming.phoneSettings,
          twilioAccountSid: incoming.phoneSettings?.twilioAccountSid?.trim()
            ? incoming.phoneSettings.twilioAccountSid
            : (local.phoneSettings?.twilioAccountSid ?? ""),
          twilioAuthToken: local.phoneSettings?.twilioAuthToken ?? "",
        }),
  }, opts.localDeskPrefs);
}

export type BackupCardSummary = {
  company: string;
  cashCad: number;
  currency: CurrencyCode;
  cashLabel: string;
  missionCount: number;
  timestampUtc: string;
  timestampLabel: string;
};

/** Short desk card: company, cash, mission count, timestamp. */
export function backupCardSummary(snapshot: SyncSnapshot): BackupCardSummary {
  const p = snapshot.payload;
  const currency = hydrateCurrency(p.catalogs?.currency);
  const cashCad = Number.isFinite(p.account?.balanceCad) ? p.account.balanceCad : 0;
  const when = snapshot.exportedAtUtc ? new Date(snapshot.exportedAtUtc) : new Date();
  const valid = !Number.isNaN(when.getTime());
  return {
    company: p.company?.operatorName?.trim() || "—",
    cashCad,
    currency,
    cashLabel: `${currency} ${formatMoney(cashCad, currency)}`,
    missionCount: Array.isArray(p.missions) ? p.missions.length : 0,
    timestampUtc: valid ? when.toISOString() : snapshot.exportedAtUtc,
    timestampLabel: valid ? formatUtcDateTime(when) : snapshot.exportedAtUtc,
  };
}

export function backupSummaryLine(summary: BackupCardSummary): string {
  const missions = summary.missionCount === 1 ? "1 mission" : `${summary.missionCount} missions`;
  return [summary.company, summary.cashLabel, missions, summary.timestampLabel].join(" · ");
}

export function snapshotSummary(snapshot: SyncSnapshot): string {
  const p = snapshot.payload;
  const card = backupCardSummary(snapshot);
  const open = p.missions.filter((m) => m.status === "pending" || m.status === "in-progress").length;
  const openSquawks = p.squawks.filter((s) => s.status === "open").length;
  const inbox = liveCompanyInbox(p.inbox);
  return [
    backupSummaryLine(card),
    `exported ${snapshot.exportedAtUtc}`,
    `fleet ${p.fleet.length}`,
    `ledger ${p.account.transactions.length} txn`,
    `logs ${p.flightLogs.length}`,
    `duty ${p.duty.sessions.length}`,
    `crew ${p.crew.length}`,
    `squawks ${openSquawks} open`,
    `dest ${p.scenery.length}`,
    `inbox ${inbox.length}`,
    `open trips ${open}`,
    `assign ${(p.assignments ?? []).length}`,
    `presence ${(p.crewPresence ?? []).length}`,
    `dispatch ${p.dispatchPlan?.status ?? "—"}`,
    snapshot.includeSecrets ? "includes API keys" : "keys redacted",
  ].join(" · ");
}

export function snapshotFilename(now = new Date()): string {
  const y = now.getUTCFullYear();
  const m = String(now.getUTCMonth() + 1).padStart(2, "0");
  const d = String(now.getUTCDate()).padStart(2, "0");
  return `northstar-dispatch-backup-${y}${m}${d}.json`;
}
